/* Partner Hub: ask the team for product photos (no order needed) and receive them here. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCircle2, Clock, Loader2, Send, X } from "lucide-react";
import { papi, errText } from "./portalApi";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const Img = ({ f }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    papi("get", `/photo-request-files/${f.id}`, undefined, { responseType: "blob" }).then(b => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [f.id]);
  return url ? <a href={url} download={f.filename} target="_blank" rel="noreferrer" data-testid={`portal-pr-img-${f.id}`}>
    <img src={url} alt={f.filename} className="h-24 w-24 rounded-lg border object-cover" /></a>
    : <div className="h-24 w-24 animate-pulse rounded-lg bg-slate-200" />;
};

const NewRequest = ({ onDone }) => {
  const [product, setProduct] = useState("");
  const [note, setNote] = useState("");
  const [sug, setSug] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (product.trim().length < 2) { setSug([]); return undefined; }
    const t = setTimeout(() => papi("get", `/product-suggest?q=${encodeURIComponent(product.trim())}`).then(r => setSug(r.rows)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [product]);
  const send = async () => {
    setBusy(true);
    try { await papi("post", "/photo-requests", { product, note }); toast.success("Request sent to the packing team"); setProduct(""); setNote(""); onDone(); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2 rounded-xl border bg-white p-3" data-testid="portal-pr-new">
      <b className="text-sm">Ask for product photos</b>
      <p className="text-xs text-slate-500">No order needed — e.g. to show availability to your customer.</p>
      <div className="relative">
        <input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Product name (type or pick)" list="pr-suggest"
          className="w-full rounded-lg border px-3 py-2 text-sm" data-testid="portal-pr-product" />
        <datalist id="pr-suggest">{sug.map(s => <option key={s} value={s} />)}</datalist>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What do you need? e.g. strip front + back, expiry date"
        className="w-full rounded-lg border px-3 py-2 text-sm" data-testid="portal-pr-note" />
      <button type="button" onClick={send} disabled={busy || product.trim().length < 2} data-testid="portal-pr-send"
        className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request</button>
    </div>
  );
};

const Row = ({ r, reload }) => {
  const cancel = async () => { try { await papi("delete", `/photo-requests/${r.id}`); reload(); } catch (e) { toast.error(errText(e)); } };
  const fresh = async () => { try { await papi("post", `/photo-requests/${r.id}/fresh`); toast.success("Sent to the packing team for fresh photos"); reload(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="space-y-2 rounded-xl border bg-white p-3" data-testid={`portal-pr-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm"><b>{r.product}</b><span className="text-xs text-slate-500">{when(r.created_at)}</span>
        {r.from_library && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800" data-testid={`portal-pr-library-${r.id}`}>From photo library · instant</span>}
        {r.status === "sent" && <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"><CheckCircle2 className="h-3.5 w-3.5" /> Photos ready</span>}
        {r.status === "open" && <><span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"><Clock className="h-3.5 w-3.5" /> Waiting</span>
          <button type="button" onClick={cancel} className="rounded p-1 text-slate-400 hover:text-rose-600" title="Cancel request" data-testid={`portal-pr-cancel-${r.id}`}><X className="h-4 w-4" /></button></>}
        {r.status === "cancelled" && <span className="ml-auto text-[11px] text-slate-400">Cancelled</span>}
      </div>
      {r.note && <p className="text-xs text-slate-600">{r.note}</p>}
      {r.reply_note && <p className="rounded-lg bg-slate-100 px-2 py-1 text-xs">Team: {r.reply_note}</p>}
      {r.files?.length > 0 && <div className="flex flex-wrap gap-2">{r.files.map(f => <Img key={f.id} f={f} />)}</div>}
      {r.from_library && r.status === "sent" && <button type="button" onClick={fresh} data-testid={`portal-pr-fresh-${r.id}`}
        className="rounded-lg border border-teal-600 px-3 py-1.5 text-xs font-semibold text-teal-700">Need fresh photos</button>}
    </div>
  );
};

export const PortalPhotoRequests = ({ onUnseen }) => {
  const [d, setD] = useState(null);
  const load = () => papi("get", "/photo-requests").then(r => {
    setD(r);
    if (r.unseen) papi("post", "/photo-requests/seen").then(() => onUnseen?.(0)).catch(() => {});
  }).catch((e) => setD({ rows: [], err: errText(e) }));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="portal-photo-requests">
      <div className="flex items-center gap-2"><Camera className="h-4 w-4 text-teal-700" /><b className="text-sm">Product photos</b></div>
      <NewRequest onDone={load} />
      {!d ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : d.err ? <p className="text-sm text-rose-600">{d.err}</p>
        : !d.rows.length ? <p className="rounded-xl border bg-white p-6 text-center text-sm text-slate-400" data-testid="portal-pr-empty">No photo requests yet.</p>
          : d.rows.map(r => <Row key={r.id} r={r} reload={load} />)}
    </div>
  );
};
